// ============================================================================
// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BrandGiveawayWizard.jsx
// Название: Brand Giveaway Wizard - CPL Lead Generation
// Версия: 4.1.0
// Дата: 25.01.2026
// ============================================================================
//
// ОПИСАНИЕ:
// Wizard для создания Brand Giveaway кампаний с CPL моделью.
// 3 шага: Настройки → Генерация → Раздача
//
// ИЗМЕНЕНИЯ v4.1:
// - Убран CampaignStatsPanel (дублирование)
// - Добавлен блок промокодов (main + referral)
// - Краткая сводка вместо воронки
// - Ссылка на подробную аналитику
//
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';

// ============================================================================
// СЕКЦИЯ 1: КОНФИГУРАЦИЯ СТИЛЕЙ AI (8 стилей в 2 категориях)
// ============================================================================

const BRAND_STYLES = {
  universal: [
    { id: 'gift', name: 'Подарок', emoji: '🎁', desc: 'Универсальный', isDefault: true },
    { id: 'festive', name: 'Праздничный', emoji: '🎉', desc: 'Яркий и весёлый' },
    { id: 'premium', name: 'Премиум', emoji: '👑', desc: 'Элегантный люкс' },
    { id: 'romantic', name: 'Романтичный', emoji: '💝', desc: 'Февраль-март' }
  ],
  business: [
    { id: 'corporate', name: 'Корпоративный', emoji: '💼', desc: 'Для B2B компаний' },
    { id: 'tech', name: 'Технологии', emoji: '🚀', desc: 'IT и стартапы' },
    { id: 'shopping', name: 'Шоппинг', emoji: '🛍️', desc: 'E-commerce' },
    { id: 'sale', name: 'Распродажа', emoji: '🔥', desc: 'Акции и скидки' }
  ]
};

const CATEGORY_LABELS = {
  universal: '⭐ Универсальные',
  business: '🏢 По типу бизнеса'
};

// CPL цена за лид (EUR)
const CPL_PRICE = 20;

// ============================================================================
// СЕКЦИЯ 2: ГЛАВНЫЙ КОМПОНЕНТ
// ============================================================================

const BrandGiveawayWizard = () => {
  const { id: campaignId } = useParams();
  const navigate = useNavigate();

  // ==========================================================================
  // 2.1 STATE
  // ==========================================================================
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Данные
  const [campaign, setCampaign] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [wallet, setWallet] = useState(null); // Brand Wallet EUR
  const [promocodes, setPromocodes] = useState({ main: [], referral: [] }); // Промокоды
  
  // Модальное окно превью
  const [previewGift, setPreviewGift] = useState(null);
  
  // Конфигурация кампании
  const [config, setConfig] = useState({
    leadCount: 10,        // Количество лидов (не подарков)
    giftAmount: 10,       // Сумма DAR для подарка
    aiStyle: 'gift',
    brandName: '',
    expiryDays: 30,
    customTitle: '',      // Заголовок на картинке
    customSubtitle: '',   // Подзаголовок на картинке
    generatePromocodes: true
  });

  // Аналитика
  const [analytics, setAnalytics] = useState({
    created: 0,
    sent: 0,
    viewed: 0,
    claimed: 0,
    spent: 0
  });

  // ==========================================================================
  // 2.2 ЗАГРУЗКА ДАННЫХ
  // ==========================================================================
  
  useEffect(() => {
    loadData();
  }, [campaignId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Загружаем кампанию
      const campRes = await api.get(`/campaigns/${campaignId}`);
      const campaignData = campRes.data || campRes;
      setCampaign(campaignData);
      
      // Восстанавливаем конфиг из brand_config
      if (campaignData.brand_config) {
        setConfig(prev => ({
          ...prev,
          brandName: campaignData.name || '',
          ...campaignData.brand_config,
          // Маппинг старого giftCount на новый leadCount
          leadCount: campaignData.brand_config.leadCount || campaignData.brand_config.giftCount || 10
        }));
      } else {
        setConfig(prev => ({
          ...prev,
          brandName: campaignData.name || ''
        }));
      }

      // Загружаем Brand Wallet (EUR)
      try {
        const walletRes = await api.get('/brand-wallet');
        setWallet(walletRes.wallet || walletRes.data?.wallet);
      } catch (e) {
        console.log('Wallet not loaded:', e.message);
      }

      // Загружаем подарки
      const giftsRes = await api.get(`/brand-gifts/campaign/${campaignId}/gifts`);
      const giftsData = giftsRes.data?.gifts || giftsRes.gifts || [];
      setGifts(giftsData);
      
      // Определяем текущий шаг по состоянию подарков
      const readyGifts = giftsData.filter(g => g.status === 'ready' && g.image_url);
      const claimedGifts = giftsData.filter(g => g.status === 'claimed');
      const pendingGifts = giftsData.filter(g => g.status === 'pending_generation');
      
      if (readyGifts.length > 0 || claimedGifts.length > 0) {
        setStep(3);
        loadAnalytics();
        loadPromocodes();
      } else if (pendingGifts.length > 0) {
        setStep(2);
      }

    } catch (err) {
      console.error('Load error:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const statsRes = await api.get(`/brand-gifts/campaign/${campaignId}/stats`);
      const stats = statsRes.stats || statsRes.data?.stats || {};
      setAnalytics({
        created: stats.totalGifts || gifts.length || 0,
        sent: stats.sentGifts || 0,
        viewed: stats.viewedGifts || 0,
        claimed: stats.claimedGifts || 0,
        spent: stats.claimedAmount || 0
      });
    } catch (e) {
      const readyGifts = gifts.filter(g => g.status === 'ready');
      const claimedGifts = gifts.filter(g => g.status === 'claimed');
      setAnalytics({
        created: readyGifts.length,
        sent: 0,
        viewed: 0,
        claimed: claimedGifts.length,
        spent: claimedGifts.reduce((sum, g) => sum + parseFloat(g.total_amount || 0), 0)
      });
    }
  };

  const loadPromocodes = async () => {
    try {
      const res = await api.get(`/promocodes/campaign/${campaignId}`);
      const allCodes = res.data?.promocodes || res.promocodes || [];
      setPromocodes({
        main: allCodes.filter(p => p.type === 'main'),
        referral: allCodes.filter(p => p.type === 'referral')
      });
    } catch (e) {
      console.log('Promocodes not loaded:', e.message);
    }
  };
  
  // ==========================================================================
  // 2.3 ОБРАБОТЧИКИ ДЕЙСТВИЙ
  // ==========================================================================

  const handleStartGeneration = async () => {
    // Валидация
    if (config.leadCount < 1 || config.leadCount > 1000) {
      setError('Количество лидов: от 1 до 1000');
      return;
    }
    
    const totalCostEur = config.leadCount * CPL_PRICE;
    const availableEur = parseFloat(wallet?.available_balance || 0);
    
    if (totalCostEur > availableEur) {
      setError(`Недостаточно средств. Нужно €${totalCostEur}, доступно €${availableEur.toFixed(2)}`);
      return;
    }

    setGenerating(true);
    setError('');
    setGenerationProgress(0);
    setStep(2);

    try {
      // 1. Сохраняем конфиг кампании
      await api.put(`/campaigns/${campaignId}`, {
        brand_config: {
          ...config,
          giftCount: config.leadCount, // для совместимости
          configured: true
        }
      });
      setGenerationProgress(10);

      // 2. Создаём seed подарки
      await api.post('/brand-gifts/create-seeds', {
        campaignId: parseInt(campaignId),
        count: config.leadCount,
        totalAmount: config.giftAmount,
        aiStyle: config.aiStyle
      });
      setGenerationProgress(30);

      // 3. Запускаем AI генерацию картинок
      await api.post(`/brand-gifts/campaign/${campaignId}/generate-all`, {
        aiStyle: config.aiStyle,
        brandName: config.brandName || campaign?.name || 'DARY',
        brandUrl: 'dary.one',
        campaignType: 'giveaway',
        lang: 'ru',
        customTitle: config.customTitle,
        customSubtitle: config.customSubtitle
      });
      setGenerationProgress(80);

      // 4. Генерируем промокоды
      if (config.generatePromocodes) {
        const prefix = config.brandName?.substring(0, 6).toUpperCase() || 'BRAND';
        
        await api.post('/promocodes/generate', {
          campaign_id: parseInt(campaignId),
          prefix: prefix,
          count: config.leadCount,
          type: 'main'
        });
        setGenerationProgress(90);
        
        await api.post('/promocodes/generate', {
          campaign_id: parseInt(campaignId),
          prefix: prefix + '-REF',
          count: config.leadCount * 6,
          type: 'referral'
        });
        setGenerationProgress(95);
      }
      setGenerationProgress(100);

      // 5. Обновляем данные
      await loadData();
      setSuccess('Кампания успешно создана!');
      setStep(3);

    } catch (err) {
      console.error('Generation error:', err);
      setError(err.response?.data?.error || 'Ошибка генерации');
      setStep(1);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm('Удалить кампанию?\n\nНеиспользованные средства вернутся на баланс.');
    if (!confirmed) return;

    try {
      await api.delete(`/campaigns/${campaignId}`);
      navigate('/business/brand/campaigns');
    } catch (err) {
      console.error('Delete error:', err);
      setError('Ошибка удаления');
    }
  };

  const downloadImage = (gift, index = 0) => {
    const link = document.createElement('a');
    link.href = gift.image_url;
    link.download = `Brand_Gift_${index + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllZip = async () => {
    setSuccess('Скачивание картинок...');
    for (let i = 0; i < readyGifts.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 300));
      downloadImage(readyGifts[i], i);
    }
    setSuccess(`Скачано ${readyGifts.length} картинок`);
  };

  const copyCode = (gift) => {
    const code = gift.shortCode || gift.short_code || gift.gift_code;
    if (code) {
      navigator.clipboard.writeText(code);
      setSuccess(`Код скопирован: ${code}`);
      setTimeout(() => setSuccess(''), 2000);
    }
  };

  const copyLink = (gift) => {
    const code = gift.shortCode || gift.short_code || gift.gift_code;
    const link = `https://dary.one/claim/${code}`;
    navigator.clipboard.writeText(link);
    setSuccess(`Ссылка скопирована`);
    setTimeout(() => setSuccess(''), 2000);
  };

  const getGiftCode = (gift, index) => `Brand_Gift_${index + 1}`;
  const getGiftAmount = (gift) => parseFloat(gift.total_amount || gift.totalAmount || 0).toFixed(0);

  // ==========================================================================
  // 2.4 СТИЛИ
  // ==========================================================================
  
  const s = {
    container: { padding: 24, maxWidth: 900, margin: '0 auto', minHeight: '100vh' },
    
    // Header
    header: { textAlign: 'center', marginBottom: 24 },
    badge: { 
      display: 'inline-block', 
      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', 
      color: 'white', padding: '8px 20px', borderRadius: 20, 
      fontSize: 13, fontWeight: 600, marginBottom: 12 
    },
    title: { fontSize: 28, fontWeight: 700, margin: '8px 0', color: '#1f2937' },
    subtitle: { color: '#6b7280', fontSize: 15 },

    // Steps
    steps: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0, marginBottom: 32 },
    stepItem: { display: 'flex', alignItems: 'center', gap: 8 },
    stepNum: { 
      width: 40, height: 40, borderRadius: '50%', 
      display: 'flex', alignItems: 'center', justifyContent: 'center', 
      fontWeight: 700, fontSize: 15 
    },
    stepActive: { background: '#10B981', color: 'white' },
    stepDone: { background: '#10B981', color: 'white' },
    stepPending: { background: '#f3f4f6', color: '#9ca3af' },
    stepLabel: { fontSize: 14, fontWeight: 600, color: '#374151' },
    stepLine: { width: 50, height: 3, background: '#e5e7eb', margin: '0 8px', borderRadius: 2 },
    stepLineDone: { background: '#10B981' },

    // Card
    card: { background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 20 },
    cardTitle: { fontSize: 18, fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, color: '#1f2937' },

    // Form
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 },
    formGroup: { marginBottom: 16 },
    label: { display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 },
    input: { 
      width: '100%', padding: '12px 14px', border: '2px solid #e5e7eb', 
      borderRadius: 10, fontSize: 15, boxSizing: 'border-box', outline: 'none' 
    },
    select: { 
      width: '100%', padding: '12px 14px', border: '2px solid #e5e7eb', 
      borderRadius: 10, fontSize: 15, background: 'white', cursor: 'pointer', boxSizing: 'border-box' 
    },
    inputHint: { fontSize: 12, color: '#9ca3af', marginTop: 4 },

    // Style Grid
    styleGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 8 },
    styleCard: { 
      padding: '14px 10px', background: '#f9fafb', border: '2px solid #e5e7eb', 
      borderRadius: 10, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s' 
    },
    styleCardSelected: { borderColor: '#10B981', background: '#f0fdf4' },
    styleName: { fontSize: 13, fontWeight: 600, marginBottom: 2 },
    styleDesc: { fontSize: 10, color: '#6b7280' },

    // Summary
    summary: { 
      background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)', 
      borderRadius: 12, padding: 20, marginTop: 20, border: '2px solid #86efac' 
    },
    summaryRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' },
    summaryLabel: { color: '#166534', fontSize: 14 },
    summaryValue: { fontWeight: 700, fontSize: 16, color: '#166534' },
    summaryTotal: { borderTop: '2px solid #86efac', marginTop: 10, paddingTop: 12 },

    // Buttons
    actions: { display: 'flex', gap: 12, marginTop: 20 },
    btn: { 
      flex: 1, padding: '14px 20px', borderRadius: 10, fontSize: 15, 
      fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'all 0.2s' 
    },
    btnPrimary: { background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white' },
    btnSecondary: { background: '#f3f4f6', color: '#374151' },
    btnDanger: { background: '#fef2f2', color: '#dc2626' },
    btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },

    // Alerts
    error: { background: '#fef2f2', color: '#dc2626', padding: 14, borderRadius: 10, marginBottom: 16, textAlign: 'center', fontSize: 14 },
    success: { background: '#f0fdf4', color: '#166534', padding: 14, borderRadius: 10, marginBottom: 16, textAlign: 'center', fontSize: 14 },

    // Wallet Info
    walletInfo: { 
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', 
      background: '#f0fdf4', borderRadius: 8, marginBottom: 16, border: '1px solid #86efac' 
    },
    walletLabel: { fontSize: 13, color: '#166534' },
    walletValue: { fontSize: 15, fontWeight: 700, color: '#166534' },

    // Loading
    loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300 },
    spinner: { fontSize: 40, animation: 'spin 1s linear infinite' },

    // Progress
    progress: { textAlign: 'center', padding: '40px 20px' },
    progressIcon: { fontSize: 48, marginBottom: 16 },
    progressTitle: { fontSize: 22, fontWeight: 700, color: '#1f2937', marginBottom: 8 },
    progressSubtitle: { color: '#6b7280', fontSize: 15, marginBottom: 24 },
    progressBar: { width: '100%', maxWidth: 400, height: 10, background: '#e5e7eb', borderRadius: 5, overflow: 'hidden', margin: '0 auto' },
    progressFill: { height: '100%', background: 'linear-gradient(90deg, #10B981, #059669)', borderRadius: 5, transition: 'width 0.5s ease' },
    progressPercent: { fontSize: 16, fontWeight: 700, color: '#10B981', marginTop: 12 },

    // Modal
    modal: { 
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
      background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', 
      justifyContent: 'center', zIndex: 1000, padding: 20 
    },
    modalContent: { 
      background: 'white', borderRadius: 16, padding: 20, 
      maxWidth: 500, width: '100%', position: 'relative' 
    },
    modalClose: { 
      position: 'absolute', top: 12, right: 12, width: 32, height: 32, 
      border: 'none', background: '#f3f4f6', borderRadius: '50%', 
      cursor: 'pointer', fontSize: 16 
    },
    modalImage: { width: '100%', borderRadius: 12, marginBottom: 16 },
    modalInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    modalCode: { fontSize: 16, fontWeight: 700 },
    modalAmount: { fontSize: 14, color: '#10B981', fontWeight: 600 },
    modalActions: { display: 'flex', gap: 10 },
    modalBtn: { flex: 1, padding: '10px 16px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', border: 'none', fontSize: 13 },

    // Funnel
    funnel: { display: 'flex', alignItems: 'center', justifyContent: 'space-around', padding: '16px 0', flexWrap: 'wrap', gap: 10 },
    funnelStep: { textAlign: 'center', minWidth: 70 },
    funnelValue: { fontSize: 28, fontWeight: 700 },
    funnelLabel: { fontSize: 12, color: '#6b7280' },
    funnelArrow: { fontSize: 20, color: '#d1d5db' },

    // Channels
    channels: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
    channel: { 
      display: 'flex', alignItems: 'center', gap: 12, padding: 16, 
      background: '#f9fafb', borderRadius: 12, cursor: 'pointer', 
      border: '2px solid transparent', transition: 'all 0.2s' 
    },
    channelIcon: { fontSize: 28 },
    channelContent: { flex: 1 },
    channelTitle: { fontSize: 14, fontWeight: 600, marginBottom: 2 },
    channelDesc: { fontSize: 12, color: '#6b7280' },
    channelBadge: { fontSize: 10, color: '#9ca3af', marginTop: 4 },

    // Gifts Grid
    giftsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    giftsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 },
    giftCard: { 
      background: '#f9fafb', borderRadius: 12, overflow: 'hidden', 
      border: '2px solid transparent', transition: 'all 0.2s' 
    },
    giftImage: { width: '100%', aspectRatio: '1', objectFit: 'cover', cursor: 'pointer', transition: 'transform 0.2s' },
    giftInfo: { padding: 10 },
    giftCode: { fontSize: 12, fontWeight: 600, marginBottom: 4 },
    giftAmount: { fontSize: 13, color: '#10B981', fontWeight: 600 },
    giftStatus: { fontSize: 10, padding: '2px 6px', borderRadius: 4, marginTop: 4, display: 'inline-block' },
    giftBtns: { display: 'flex', gap: 6, marginTop: 8 },
    giftBtn: { flex: 1, padding: '6px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }
  };

  // ==========================================================================
  // 2.5 ВЫЧИСЛЕНИЯ
  // ==========================================================================
  
  const readyGifts = gifts.filter(g => g.status === 'ready' && g.image_url);
  const claimedGifts = gifts.filter(g => g.status === 'claimed');
  const totalCostEur = config.leadCount * CPL_PRICE;
  const availableEur = parseFloat(wallet?.available_balance || 0);
  const claimRate = analytics.created > 0 ? ((analytics.claimed / analytics.created) * 100).toFixed(1) : 0;
  const canGenerate = availableEur >= totalCostEur && !generating;

  // ==========================================================================
  // 2.6 RENDER: LOADING
  // ==========================================================================
  
  if (loading) {
    return (
      <div style={s.container}>
        <div style={s.loading}>
          <div style={s.spinner}>⏳</div>
          <p style={{color: '#6b7280', fontSize: 15}}>Загрузка кампании...</p>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // 2.7 RENDER: MAIN
  // ==========================================================================
  
  return (
    <div style={s.container}>
      
      {/* MODAL PREVIEW */}
      {previewGift && (
        <div style={s.modal} onClick={() => setPreviewGift(null)}>
          <div style={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <button style={s.modalClose} onClick={() => setPreviewGift(null)}>✕</button>
            <img src={previewGift.image_url} alt="preview" style={s.modalImage} />
            <div style={s.modalInfo}>
              <div>
                <div style={s.modalCode}>{campaign?.name || 'Brand Gift'}</div>
                <div style={s.modalAmount}>{getGiftAmount(previewGift)} DAR</div>
              </div>
              <div style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                background: previewGift.status === 'claimed' ? '#fef3c7' : '#dcfce7',
                color: previewGift.status === 'claimed' ? '#92400e' : '#166534'
              }}>
                {previewGift.status === 'claimed' ? '✓ Активирован' : '● Активен'}
              </div>
            </div>
            <div style={s.modalActions}>
              <button style={{...s.modalBtn, background: '#3B82F6', color: 'white'}} onClick={() => downloadImage(previewGift)}>⬇️ Скачать</button>
              <button style={{...s.modalBtn, background: '#10B981', color: 'white'}} onClick={() => copyCode(previewGift)}>📋 Код</button>
              <button style={{...s.modalBtn, background: '#8B5CF6', color: 'white'}} onClick={() => copyLink(previewGift)}>🔗 Ссылка</button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.badge}>🎁 LEAD GENERATION</div>
        <h1 style={s.title}>{campaign?.name || 'Новая кампания'}</h1>
        <p style={s.subtitle}>Создайте подарки — раздайте клиентам — получите лидов</p>
      </div>

      {/* STEPS */}
      <div style={s.steps}>
        <div style={s.stepItem}>
          <div style={{...s.stepNum, ...(step >= 1 ? s.stepDone : s.stepPending)}}>{step > 1 ? '✓' : '1'}</div>
          <span style={s.stepLabel}>Настройки</span>
        </div>
        <div style={{...s.stepLine, ...(step > 1 ? s.stepLineDone : {})}} />
        <div style={s.stepItem}>
          <div style={{...s.stepNum, ...(step > 2 ? s.stepDone : step === 2 ? s.stepActive : s.stepPending)}}>{step > 2 ? '✓' : '2'}</div>
          <span style={s.stepLabel}>Генерация</span>
        </div>
        <div style={{...s.stepLine, ...(step > 2 ? s.stepLineDone : {})}} />
        <div style={s.stepItem}>
          <div style={{...s.stepNum, ...(step === 3 ? s.stepActive : s.stepPending)}}>3</div>
          <span style={s.stepLabel}>Раздача</span>
        </div>
      </div>

      {/* ALERTS */}
      {error && <div style={s.error}>❌ {error}</div>}
      {success && <div style={s.success}>✅ {success}</div>}

      {/* STEP 1: НАСТРОЙКИ */}
      {step === 1 && (
        <>
          {/* Wallet Info */}
          <div style={s.walletInfo}>
            <span style={s.walletLabel}>💰 Brand Wallet:</span>
            <span style={s.walletValue}>€{availableEur.toFixed(2)}</span>
            <span style={{marginLeft: 'auto', fontSize: 12, color: '#059669', cursor: 'pointer'}} onClick={() => navigate('/business/wallet')}>
              Пополнить →
            </span>
          </div>

          <div style={s.card}>
            <h3 style={s.cardTitle}>⚙️ Настройки кампании</h3>

            <div style={s.formGrid}>
              {/* Название бренда */}
              <div style={s.formGroup}>
                <label style={s.label}>Название бренда</label>
                <input
                  type="text"
                  style={s.input}
                  value={config.brandName}
                  onChange={(e) => setConfig({...config, brandName: e.target.value})}
                  placeholder="Nike, Sephora, Adidas..."
                />
                <div style={s.inputHint}>Отображается на картинках</div>
              </div>

              {/* Количество лидов */}
              <div style={s.formGroup}>
                <label style={s.label}>Количество лидов</label>
                <input
                  type="number"
                  style={s.input}
                  value={config.leadCount}
                  onChange={(e) => setConfig({...config, leadCount: parseInt(e.target.value) || 1})}
                  min="1"
                  max="1000"
                />
                <div style={s.inputHint}>От 1 до 1000 лидов</div>
              </div>

              {/* Срок действия */}
              <div style={s.formGroup}>
                <label style={s.label}>Срок действия</label>
                <select
                  style={s.select}
                  value={config.expiryDays}
                  onChange={(e) => setConfig({...config, expiryDays: parseInt(e.target.value)})}
                >
                  <option value="7">7 дней</option>
                  <option value="14">14 дней</option>
                  <option value="30">30 дней</option>
                  <option value="60">60 дней</option>
                  <option value="90">90 дней</option>
                </select>
              </div>

              {/* Сумма подарка */}
              <div style={s.formGroup}>
                <label style={s.label}>Сумма подарка (DAR)</label>
                <input
                  type="number"
                  style={s.input}
                  value={config.giftAmount}
                  onChange={(e) => setConfig({...config, giftAmount: parseInt(e.target.value) || 10})}
                  min="5"
                  max="100"
                />
                <div style={s.inputHint}>Сколько DAR получит клиент</div>
              </div>
            </div>

            {/* Заголовки для картинок */}
            <div style={{marginTop: 20, padding: 16, background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0'}}>
              <div style={{fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12}}>
                ✨ Текст на картинках
              </div>
              <div style={s.formGrid}>
                <div style={s.formGroup}>
                  <label style={s.label}>Заголовок</label>
                  <input
                    type="text"
                    style={s.input}
                    value={config.customTitle}
                    onChange={(e) => setConfig({...config, customTitle: e.target.value})}
                    placeholder="Подарок для вас!"
                  />
                  <div style={s.inputHint}>Главный текст на картинке</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Подзаголовок</label>
                  <input
                    type="text"
                    style={s.input}
                    value={config.customSubtitle}
                    onChange={(e) => setConfig({...config, customSubtitle: e.target.value})}
                    placeholder="Скидка 10% на первый заказ"
                  />
                  <div style={s.inputHint}>Дополнительный текст</div>
                </div>
              </div>
            </div>

            {/* AI Style Selector */}
            <div style={{...s.formGroup, marginTop: 8}}>
              <label style={s.label}>🎨 Стиль дизайна</label>
              
              {Object.entries(BRAND_STYLES).map(([category, styles]) => (
                <div key={category} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
                    {CATEGORY_LABELS[category]}
                  </div>
                  <div style={s.styleGrid}>
                    {styles.map(style => (
                      <div
                        key={style.id}
                        style={{
                          ...s.styleCard,
                          ...(config.aiStyle === style.id ? s.styleCardSelected : {})
                        }}
                        onClick={() => setConfig({...config, aiStyle: style.id})}
                      >
                        <div style={{ fontSize: 24, marginBottom: 4 }}>{style.emoji}</div>
                        <div style={s.styleName}>{style.name}</div>
                        <div style={s.styleDesc}>{style.desc}</div>
                        {style.isDefault && (
                          <div style={{ fontSize: 8, color: '#10B981', fontWeight: 600, marginTop: 2 }}>★ ПО УМОЛЧАНИЮ</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary - CPL Model */}
            <div style={s.summary}>
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>👥 Лидов</span>
                <span style={s.summaryValue}>{config.leadCount} чел</span>
              </div>
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>💶 CPL (цена за лид)</span>
                <span style={s.summaryValue}>€{CPL_PRICE}</span>
              </div>
              <div style={{...s.summaryRow, ...s.summaryTotal}}>
                <span style={{...s.summaryLabel, fontSize: 16, fontWeight: 700}}>💰 Итого</span>
                <span style={{
                  ...s.summaryValue, 
                  fontSize: 24,
                  color: totalCostEur > availableEur ? '#dc2626' : '#166534'
                }}>
                  €{totalCostEur}
                </span>
              </div>
              {totalCostEur > availableEur && (
                <div style={{color: '#dc2626', fontSize: 12, marginTop: 8, textAlign: 'center'}}>
                  ⚠️ Недостаточно средств. Пополните Brand Wallet.
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={s.actions}>
              <button style={{...s.btn, ...s.btnDanger}} onClick={handleDelete}>🗑️ Удалить</button>
              <button
                style={{...s.btn, ...s.btnPrimary, ...(!canGenerate ? s.btnDisabled : {})}}
                onClick={handleStartGeneration}
                disabled={!canGenerate}
              >
                🚀 Создать {config.leadCount} подарков
              </button>
            </div>
          </div>
        </>
      )}

      {/* STEP 2: ГЕНЕРАЦИЯ */}
      {step === 2 && (
        <div style={s.card}>
          <div style={s.progress}>
            <div style={s.progressIcon}>🎨</div>
            <h3 style={s.progressTitle}>
              {generating ? 'Создаём AI картинки...' : 'Генерация завершена!'}
            </h3>
            <p style={s.progressSubtitle}>
              {generating 
                ? `Создаём ${config.leadCount} уникальных подарков`
                : 'Картинки готовы к раздаче'
              }
            </p>
            <div style={s.progressBar}>
              <div style={{...s.progressFill, width: `${generationProgress}%`}} />
            </div>
            <div style={s.progressPercent}>{generationProgress}%</div>
            {generating && (
              <p style={{color: '#9ca3af', marginTop: 16, fontSize: 13}}>⏱️ Обычно 1-3 минуты</p>
            )}
          </div>
        </div>
      )}

      {/* STEP 3: РАЗДАЧА */}
      {step === 3 && (
        <>
          {/* Краткая сводка */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20
          }}>
            <div style={{background: '#f0fdf4', borderRadius: 12, padding: 16, textAlign: 'center', border: '1px solid #86efac'}}>
              <div style={{fontSize: 28, fontWeight: 700, color: '#10B981'}}>{readyGifts.length + claimedGifts.length}</div>
              <div style={{fontSize: 13, color: '#166534'}}>Всего подарков</div>
            </div>
            <div style={{background: '#fef3c7', borderRadius: 12, padding: 16, textAlign: 'center', border: '1px solid #fcd34d'}}>
              <div style={{fontSize: 28, fontWeight: 700, color: '#d97706'}}>{claimedGifts.length}</div>
              <div style={{fontSize: 13, color: '#92400e'}}>Активировано</div>
            </div>
            <div style={{background: '#eff6ff', borderRadius: 12, padding: 16, textAlign: 'center', border: '1px solid #93c5fd'}}>
              <div style={{fontSize: 28, fontWeight: 700, color: '#3B82F6'}}>{readyGifts.length}</div>
              <div style={{fontSize: 13, color: '#1d4ed8'}}>Осталось</div>
            </div>
          </div>

          {/* Gifts Grid */}
          <div style={s.card}>
            <div style={s.giftsHeader}>
              <h3 style={{...s.cardTitle, marginBottom: 0}}>🎁 Подарки ({readyGifts.length})</h3>
              <button
                style={{padding: '8px 16px', background: '#3B82F6', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13}}
                onClick={downloadAllZip}
              >
                ⬇️ Скачать все
              </button>
            </div>
            
            <div style={s.giftsGrid}>
              {readyGifts.map((gift, index) => (
                <div key={gift.id} style={s.giftCard}>
                  <img 
                    src={gift.image_url} 
                    alt={getGiftCode(gift, index)} 
                    style={s.giftImage}
                    onClick={() => setPreviewGift(gift)}
                  />
                  <div style={s.giftInfo}>
                    <div style={s.giftCode}>{getGiftCode(gift, index)}</div>
                    <div style={s.giftAmount}>{getGiftAmount(gift)} DAR</div>
                    {gift.status === 'claimed' && (
                      <div style={{...s.giftStatus, background: '#fef3c7', color: '#92400e'}}>✓ Активирован</div>
                    )}
                    <div style={s.giftBtns}>
                      <button style={{...s.giftBtn, background: '#3B82F6', color: 'white'}} onClick={() => downloadImage(gift, index)}>⬇️</button>
                      <button style={{...s.giftBtn, background: '#e5e7eb', color: '#374151'}} onClick={() => copyCode(gift)}>📋</button>
                      <button style={{...s.giftBtn, background: '#f3e8ff', color: '#7c3aed'}} onClick={() => setPreviewGift(gift)}>👁️</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Промокоды */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>🎟️ Промокоды для раздачи</h3>
            
            {/* Main промокоды */}
            <div style={{marginBottom: 20}}>
              <div style={{fontSize: 14, fontWeight: 600, color: '#10B981', marginBottom: 10}}>
                Main ({promocodes.main.length}) — для клиентов, 10 DAR
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                {promocodes.main.map(p => (
                  <div 
                    key={p.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', background: p.status === 'available' ? '#f0fdf4' : '#f3f4f6',
                      borderRadius: 8, border: '1px solid #d1d5db',
                      opacity: p.status === 'available' ? 1 : 0.6
                    }}
                  >
                    <span style={{fontFamily: 'monospace', fontSize: 13, fontWeight: 600}}>{p.code}</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(p.code); setSuccess('Скопировано: ' + p.code); setTimeout(() => setSuccess(''), 1500); }}
                      style={{background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 2}}
                    >📋</button>
                    {p.status !== 'available' && <span style={{fontSize: 10, color: '#9ca3af'}}>использован</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Referral промокоды */}
            <div>
              <div style={{fontSize: 14, fontWeight: 600, color: '#8B5CF6', marginBottom: 10}}>
                Referral ({promocodes.referral.length}) — для друзей клиентов, 5 DAR
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 150, overflowY: 'auto'}}>
                {promocodes.referral.slice(0, 20).map(p => (
                  <div 
                    key={p.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 10px', background: p.status === 'available' ? '#f5f3ff' : '#f3f4f6',
                      borderRadius: 6, border: '1px solid #ddd', fontSize: 12,
                      opacity: p.status === 'available' ? 1 : 0.6
                    }}
                  >
                    <span style={{fontFamily: 'monospace', fontWeight: 500}}>{p.code}</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(p.code); setSuccess('Скопировано'); setTimeout(() => setSuccess(''), 1500); }}
                      style={{background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0}}
                    >📋</button>
                  </div>
                ))}
                {promocodes.referral.length > 20 && (
                  <div style={{padding: '6px 10px', color: '#9ca3af', fontSize: 12}}>
                    ... ещё {promocodes.referral.length - 20}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Distribution Channels */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>📤 Каналы раздачи</h3>
            <div style={s.channels}>
              <div style={s.channel} onClick={downloadAllZip}>
                <div style={s.channelIcon}>📦</div>
                <div style={s.channelContent}>
                  <div style={s.channelTitle}>Скачать все</div>
                  <div style={s.channelDesc}>Картинки для соцсетей, мессенджеров</div>
                </div>
              </div>
              <div style={s.channel}>
                <div style={s.channelIcon}>📧</div>
                <div style={s.channelContent}>
                  <div style={s.channelTitle}>Email рассылка</div>
                  <div style={s.channelDesc}>Загрузи CSV с email</div>
                  <div style={s.channelBadge}>🚧 Скоро</div>
                </div>
              </div>
              <div style={s.channel}>
                <div style={s.channelIcon}>🔗</div>
                <div style={s.channelContent}>
                  <div style={s.channelTitle}>Уникальные ссылки</div>
                  <div style={s.channelDesc}>Для UTM отслеживания</div>
                  <div style={s.channelBadge}>🚧 Скоро</div>
                </div>
              </div>
              <div style={s.channel}>
                <div style={s.channelIcon}>🖼️</div>
                <div style={s.channelContent}>
                  <div style={s.channelTitle}>QR постеры</div>
                  <div style={s.channelDesc}>Для печати</div>
                  <div style={s.channelBadge}>🚧 Скоро</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Actions */}
          <div style={{...s.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12}}>
            <div style={s.actions}>
              <button style={{...s.btn, ...s.btnDanger}} onClick={handleDelete}>🗑️ Удалить</button>
              <button style={{...s.btn, ...s.btnSecondary}} onClick={() => setStep(1)}>⚙️ Настройки</button>
              <button style={{...s.btn, ...s.btnSecondary}} onClick={() => navigate('/business/brand/campaigns')}>← К списку</button>
            </div>
            <button 
              style={{padding: '12px 20px', background: '#10B981', color: 'white', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14}}
              onClick={() => navigate('/business/analytics')}
            >
              📊 Подробная аналитика →
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BrandGiveawayWizard;